/**
 * Inter-unit message queue.
 *
 * In the original, units send messages to the command post for events the
 * player isn't actively driving. The player reads them on demand (new list,
 * old list, Ctrl+N quick-read). We model two lists: pending (unread) and
 * archive (read but not deleted). New messages also raise a "fresh" flag
 * so the UI can play the new-message sound exactly once per arrival.
 */

export type MessageKind =
  | "enemy_spotted"
  | "unit_destroyed"
  | "unit_annihilated_no_energy"
  | "all_workers_lost"
  | "all_trucks_lost"
  | "enemy_base_found"
  | "your_base_struck"
  | "minerals_full";

export interface Message {
  readonly kind: MessageKind;
  /** Human-readable line for speech / log. */
  readonly text: string;
  /** Tick at which this message was created. */
  readonly tick: number;
}

export class MessageQueue {
  private readonly pending: Message[] = [];
  private readonly archive: Message[] = [];
  private freshFlag = false;

  /** Add a new message to the pending list and raise the fresh flag. */
  push(msg: Message): void {
    this.pending.push(msg);
    this.freshFlag = true;
  }

  /** Read and clear the fresh flag. Returns true exactly once per arrival. */
  drainFreshFlag(): boolean {
    const f = this.freshFlag;
    this.freshFlag = false;
    return f;
  }

  /** Snapshot of the pending (unread) list. */
  pendingList(): readonly Message[] {
    return this.pending;
  }

  /** Snapshot of the archived (already-read) list. */
  archiveList(): readonly Message[] {
    return this.archive;
  }

  /** Pop the next pending message and archive it. */
  readNext(): Message | null {
    const m = this.pending.shift();
    if (!m) return null;
    this.archive.push(m);
    return m;
  }

  /** Delete the most recently archived message. */
  deleteLast(): Message | null {
    return this.archive.pop() ?? null;
  }

  /** True iff there's at least one pending message. */
  hasPending(): boolean {
    return this.pending.length > 0;
  }
}
